import React from 'react';

const QuestionDisplay = ({ 
  currentQuestion, 
  currentQuestionIndex,
  exam,
  answers,
  markedForReview,
  handleAnswerChange,
  toggleMarkForReview
}) => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      {/* Question Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {/* Subject Badge */}
            {currentQuestion.subjectName && (
              <span className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 py-2 rounded-lg font-bold shadow-md">
                📚 {currentQuestion.subjectName}
              </span>
            )}
            
            {/* Section Badge (if exam has sections) */}
            {exam?.sections && exam.sections.length > 1 && (
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg font-bold">
                {currentQuestion.sectionName || exam.sections[currentQuestion.sectionId || 0]?.name || `Section ${(currentQuestion.sectionId || 0) + 1}`}
              </span>
            )}
            <span className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-lg font-bold">
              Q{currentQuestionIndex + 1}
            </span>
            <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-lg text-sm font-semibold">
              {currentQuestion.marks} Marks
            </span>
            <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${
              currentQuestion.difficultyLevel === 'easy' ? 'bg-green-100 text-green-800' :
              currentQuestion.difficultyLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {currentQuestion.difficultyLevel}
            </span>
          </div>
          
          {/* Question Text */}
          <div className="text-lg text-gray-900 mb-4">
            {currentQuestion.questionText}
          </div>

          {/* Question Image */}
          {currentQuestion.questionImage && (
            <img 
              src={currentQuestion.questionImage} 
              alt="Question"
              className="max-w-md rounded-lg shadow-md mb-6"
            />
          )}
        </div>

        {/* Mark for Review Button */}
        <button
          onClick={() => toggleMarkForReview(currentQuestionIndex)}
          className={`ml-4 px-4 py-3 rounded-lg font-semibold transition flex items-center gap-2 ${
            markedForReview.has(currentQuestionIndex)
              ? 'bg-purple-600 text-white'
              : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
          }`}
          title="Mark for Review"
        >
          <svg className="h-5 w-5" fill={markedForReview.has(currentQuestionIndex) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          <span>{markedForReview.has(currentQuestionIndex) ? 'Marked' : 'Mark for Review'}</span>
        </button>
      </div>

      {/* Answer Options */}
      <div className="space-y-4">
        {currentQuestion.questionType === 'mcq' && (
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => {
              // Use originalIndex if available (for shuffled options), otherwise use current index
              // This ensures the answer maps to the correct original option
              const optionIdentifier = option.originalIndex !== undefined 
                ? option.originalIndex 
                : index;
              
              // Create option value using original index to maintain consistency
              const optionValue = option.text || `option_${optionIdentifier}_${option.image ? 'img' : 'txt'}`;
              
              return (
                <label
                  key={index}
                  className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition ${
                    answers[currentQuestion.id] === optionValue
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion.id}`}
                    value={optionValue}
                    checked={answers[currentQuestion.id] === optionValue}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    className="mt-1 w-5 h-5 text-indigo-600"
                  />
                  <div className="flex-1">
                    {option.text && <span className="text-gray-900">{option.text}</span>}
                    {option.image && (
                      <img 
                        src={option.image} 
                        alt={`Option ${index + 1}`}
                        className={`${option.text ? 'mt-2' : ''} max-w-sm rounded shadow`}
                      />
                    )}
                    {!option.text && !option.image && (
                      <span className="text-gray-500 italic">Option {index + 1}</span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {currentQuestion.questionType !== 'mcq' && (
          <textarea
            value={answers[currentQuestion.id] || ''}
            onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
            placeholder="Type your answer here..."
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 resize-none"
            rows="6"
          />
        )}
      </div>
    </div>
  );
};

export default QuestionDisplay;
